import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/features/auth/AuthProvider";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { NotificationOptInSheet } from "./NotificationOptInSheet";

const STORAGE_KEY = "bc.notification_optin_shown";
const CHECK_INTERVAL_MS = 1500;

/** Pages where the prompt must never appear: public pages, auth flow and
 *  onboarding (the user has no household yet — registering would 401 or
 *  interrupt the flow). */
const EXCLUDED_PREFIXES = [
  "/login",
  "/auth",
  "/onboarding",
  "/share",
  "/legal",
  "/faq",
  "/about",
  "/cooking",
];

function isEligiblePath(pathname: string): boolean {
  if (pathname === "/" || pathname === "") return false;
  return !EXCLUDED_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

/**
 * Shows the push-notification opt-in sheet once, to signed-in users only,
 * on an in-app page (audit M21: it used to pop up on the landing and login
 * pages for anonymous visitors, where accepting could only fail).
 *
 * This provider sits above the router, so it polls `location.pathname`
 * (cheap) until the user reaches an eligible page.
 */
export function NotificationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [showSheet, setShowSheet] = useState(false);
  const { user } = useAuth();
  const { hasOptedIn, permission } = usePushNotifications();

  useEffect(() => {
    if (!user) return;
    // Nothing to ask when push cannot work here, or the browser already
    // blocked notifications (accepting in the sheet could only fail).
    if (hasOptedIn || permission === "unsupported" || permission === "denied")
      return;
    if (localStorage.getItem(STORAGE_KEY)) return;

    // Short delay so the page renders first, then wait for an in-app page.
    const timer = setInterval(() => {
      if (!isEligiblePath(window.location.pathname)) return;
      clearInterval(timer);
      setShowSheet(true);
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [user, hasOptedIn, permission]);

  const handleComplete = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, "true");
    setShowSheet(false);
  }, []);

  return (
    <>
      {children}
      <NotificationOptInSheet
        open={showSheet}
        onOpenChange={(open) => {
          setShowSheet(open);
          if (!open) {
            // User dismissed — don't show again
            localStorage.setItem(STORAGE_KEY, "true");
          }
        }}
        onComplete={handleComplete}
      />
    </>
  );
}
